import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';

import { spacing } from '@/lib/theme';

export type SpacingToken = keyof typeof spacing;

interface StackProps {
	/** Space between children, as a spacing token. Defaults to `space40` (16px). */
	gap?: SpacingToken;
	align?: 'stretch' | 'start' | 'center' | 'end';
	justify?: 'start' | 'center' | 'end' | 'space-between';
}

const flexValue = (value: string) =>
	value === 'start' || value === 'end' ? `flex-${value}` : value;

const layoutProps = new Set(['gap', 'align', 'justify', 'wrap']);
const shouldForwardProp = (prop: string) =>
	isPropValid(prop) && !layoutProps.has(prop);

/** Vertical flex column with a consistent gap. The default building block for layouts. */
export const Stack = styled('div', { shouldForwardProp })<StackProps>`
	display: flex;
	flex-direction: column;
	gap: ${({ gap = 'space40' }) => spacing[gap]};
	align-items: ${({ align = 'stretch' }) => flexValue(align)};
	justify-content: ${({ justify = 'start' }) => flexValue(justify)};
	min-width: 0;
`;

interface InlineProps extends StackProps {
	wrap?: boolean;
}

/** Horizontal flex row. Wraps by default so it degrades gracefully on narrow screens. */
export const Inline = styled('div', { shouldForwardProp })<InlineProps>`
	display: flex;
	flex-direction: row;
	flex-wrap: ${({ wrap = true }) => (wrap ? 'wrap' : 'nowrap')};
	gap: ${({ gap = 'space20' }) => spacing[gap]};
	align-items: ${({ align = 'center' }) => flexValue(align)};
	justify-content: ${({ justify = 'start' }) => flexValue(justify)};
	min-width: 0;
`;
